import BulkAddGamesModal from "../game/BulkAddGamesModal";
import EditGameModal from "../game/EditGameModal";
import GameResultModal from "../game/EnterGameResultModal";
import {
    useGamesBlankImportExcelTemplate,
    useGamesImportExcelTemplate,
    useGetEventsSeasonAnchored,
} from "./queries/GetGames";
import GameElement from "../game/GameElement";
import {useEffect, useMemo, useRef, useState, useCallback} from "react";
import {toast} from "react-toastify";
import PaginatedFlatTable from "../pagination/PaginatedFlatTable";
import TrainingElement from "../game/TrainingElement";
import BulkAddTrainingsModal from "../game/BulkAddTrainingsModal";
import ImportGamesExcelModal from "../game/ImportGamesExcelModal";
import {GlobalStyles} from "../../constants/styles";
import PrintGameSheetsModal from "../game/PrintGameSheetsModal";
import GameSheet from "../game/GameSheet";
import {createPortal} from "react-dom";
import useTranslation from "../../i18n/useTranslation";
import useActiveOrganization from "../common/hooks/useActiveOrganization";
import {gql, useQuery} from "@apollo/client";
import {getLocalizedGraphQLError} from "../../util/graphqlError";
import BulkEditGamesModal from "../game/BulkEditGamesModal";
import useHasFeature from "../common/hooks/useHasFeature";
import {GET_MANAGERS} from "../staff/queries/GetManagers";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const GET_SCHEDULE_REFEREE_ASSIGNMENTS = gql`
    query GetScheduleRefereeAssignments($eventIds: [ID!]!) {
        eventRefereeAssignmentsForEvents(eventIds: $eventIds) {
            event { id }
            referee { id }
            status
            createdAt
            invitedAt
            respondedAt
            completedAt
            assignedBy { firstName lastName email }
        }
    }
`;

export const BULK_EDIT_EVENTS = gql`
    mutation BulkEditEvents(
        $eventIds: [ID!]!
        $fieldId: Int
        $refereeIds: [Int]
        $managerIds: [Int]
        $description: String
    ) {
        bulkEditEvents(
            eventIds: $eventIds
            fieldId: $fieldId
            refereeIds: $refereeIds
            managerIds: $managerIds
            description: $description
        ) {
            success
            message

            updatedEvents {
                id
                field {
                    id
                    name
                }
                description
                responsibles
                managers
            }
        }
    }
`;

function b64ToUint8Array(base64) {
    const binary = window.atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

export default function Fixtures({
                                     stages,
                                     division,
                                     activeSeasonData,
                                     games,
                                     setGames,
                                     setTotalGames,
                                 }) {
    const {t} = useTranslation();
    const {activeSubscription} = useActiveOrganization();
    const {hasFeature} = useHasFeature(activeSubscription);

    const organizationId = division?.organization?.id;
    const {data: managersData} = useQuery(GET_MANAGERS, {
        variables: {q: "", page: 1, pageSize: 1000},
        skip: !organizationId,
        fetchPolicy: "cache-and-network",
    });
    const managerOptions = useMemo(() => {
        if (Array.isArray(division?.managers)) return division.managers;

        return (managersData?.managers?.objects || []).filter((manager) =>
            manager.profileOrganizations?.some((profileOrganization) =>
                String(profileOrganization.organization?.id) === String(organizationId)
            )
        );
    }, [division?.managers, managersData?.managers?.objects, organizationId]);

    const [getEventsSeasonAnchored] = useGetEventsSeasonAnchored();

    const [selectedEventIds, setSelectedEventIds] = useState([]);

    const [loadGamesImportTemplate, {loading: gamesTemplateLoading}] =
        useGamesImportExcelTemplate();

    const [loadGamesBlankImportTemplate, {loading: gamesBlankTemplateLoading}] =
        useGamesBlankImportExcelTemplate();

    const templateLoading = gamesTemplateLoading || gamesBlankTemplateLoading;

    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const lastSeasonIdRef = useRef(null);
    const lastPageSizeRef = useRef(25);

    const [printModalOpen, setPrintModalOpen] = useState(false);
    const [gamesToPrint, setGamesToPrint] = useState([]);
    const [printRoot, setPrintRoot] = useState(null);

    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [selectedTemplateStageId, setSelectedTemplateStageId] = useState("");
    const [templateMaxRows, setTemplateMaxRows] = useState(400);
    const [selectedTemplateType, setSelectedTemplateType] = useState("dropdown");

    const resolveAnchorDateISO = useCallback(() => {
        const now = new Date();

        const start = activeSeasonData?.startDate
            ? new Date(activeSeasonData.startDate)
            : null;

        if (start && now < start) return start.toISOString();

        return now.toISOString();
    }, [activeSeasonData?.startDate]);

    const fetchGames = useCallback(
        async (p = null, {useAnchor = false} = {}) => {
            if (!activeSeasonData?.slug) return;

            const anchorDate = resolveAnchorDateISO();

            const variables = {
                seasonSlug: activeSeasonData.slug,
                stageId: undefined,
                pageSize,
                anchorDate,
            };

            if (!useAnchor) {
                const safePage = Math.max(1, parseInt(p || 1, 10));
                variables.page = safePage;
            }

            setLoading(true);

            try {
                const res = await getEventsSeasonAnchored({
                    variables,
                    fetchPolicy: "network-only",
                });

                const payload = res.data?.eventsBySeason;

                setGames(payload?.objects ?? []);
                setPages(payload?.pages ?? 1);
                setPage(payload?.page ?? 1);
                setTotalGames(payload?.totalCount ?? 0);
            } catch (err) {
                toast.error(
                    getLocalizedGraphQLError(err, t, "fixtures.messages.failedToLoadEvents"),
                    {
                        autoClose: 3000,
                        toastId: "games_query_error",
                        position: "top-center",
                        closeOnClick: true,
                    }
                );
            } finally {
                setLoading(false);
            }
        },
        [
            activeSeasonData?.slug,
            getEventsSeasonAnchored,
            pageSize,
            resolveAnchorDateISO,
            setGames,
            setPages,
            setPage,
            setTotalGames,
            t,
        ]
    );

    useEffect(() => {
        if (typeof document !== "undefined") {
            const el = document.getElementById("kbola-print-area");
            setPrintRoot(el || null);
        }
    }, []);

    useEffect(() => {
        const seasonId = activeSeasonData?.id;
        if (!seasonId) return;

        if (lastSeasonIdRef.current === seasonId) return;
        lastSeasonIdRef.current = seasonId;

        fetchGames(null, {useAnchor: true});

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSeasonData?.id]);

    useEffect(() => {
        if (!activeSeasonData?.slug) return;
        if (lastPageSizeRef.current === pageSize) return;
        lastPageSizeRef.current = pageSize;
        fetchGames(1, {useAnchor: false});
    }, [activeSeasonData?.slug, fetchGames, pageSize]);

    const handleRemoveGame = useCallback(
        (gameId) => {
            setGames((prev) => prev.filter((g) => g.id !== gameId));
        },
        [setGames]
    );

    const handleRemoveEvent = useCallback(
        (id) => {
            setGames((prev) => prev.filter((e) => e.id !== id));
        },
        [setGames]
    );

    const updateScore = useCallback(
        (gameId, scoreHome, scoreVisitor, status = "COMPLETED", forfeitTeam = null, inningScores = null, penaltiesHome = null, penaltiesVisitor = null) => {
            setGames((prev) =>
                prev.map((g) =>
                    String(g?.game?.id) === String(gameId)
                        ? {
                            ...g,
                            status,
                            game: {
                                ...g.game,
                                scoreHome: parseInt(scoreHome, 10),
                                scoreVisitor: parseInt(scoreVisitor, 10),
                                penaltiesHome,
                                penaltiesVisitor,
                                forfeitTeam,
                                ...(inningScores ? {inningScores} : {}),
                            },
                        }
                        : g
                )
            );
        },
        [setGames]
    );

    const allGames = games;
    const scheduleGameIds = useMemo(
        () => (allGames || []).filter((event) => event?.eventType === "GAME").map((event) => event.id),
        [allGames]
    );
    const {data: scheduleAssignmentData} = useQuery(
        GET_SCHEDULE_REFEREE_ASSIGNMENTS,
        {
            variables: {eventIds: scheduleGameIds},
            skip: !hasFeature("elite_referee_management_dashboard") || scheduleGameIds.length === 0,
            fetchPolicy: "network-only",
        }
    );
    const assignmentStatusByEvent = useMemo(() => {
        const statuses = {};
        (scheduleAssignmentData?.eventRefereeAssignmentsForEvents || []).forEach((assignment) => {
            const eventId = assignment.event.id;
            const current = statuses[eventId] || [];
            const status = String(assignment.status || "").toLowerCase();
            if (status && !current.includes(status)) current.push(status);
            statuses[eventId] = current;
        });
        return statuses;
    }, [scheduleAssignmentData]);
    const getScheduleAssignmentStatus = useCallback((event) => {
        const statuses = assignmentStatusByEvent[event.id] || [];
        if (statuses.includes("completed")) return "completed";
        if (statuses.includes("accepted")) return "accepted";
        if (statuses.includes("invited")) return "invited";
        if (statuses.includes("proposed")) return "proposed";
        if (statuses.includes("declined")) return "declined";
        if ((event.responsibles || []).length > 0) return "manual";
        return "unassigned";
    }, [assignmentStatusByEvent]);
    const visibleGames = useMemo(() => allGames || [], [allGames]);

    const groupedForModal = useMemo(() => {
        const map = new Map();

        for (const e of games || []) {
            if (e?.eventType !== "GAME") continue;

            const md = e?.game?.matchDay;
            if (md == null || md === "") continue;

            if (!map.has(md)) {
                map.set(md, {matchDay: md, games: []});
            }

            map.get(md).games.push(e);
        }

        return Array.from(map.values());
    }, [games]);

    const templateStageOptions = useMemo(
        () =>
            (stages || [])
                .filter((stage) => stage?.id)
                .map((stage) => ({
                    id: String(stage.id),
                    name: stage?.name || `#${stage.id}`,
                })),
        [stages]
    );

    useEffect(() => {
        if (!templateStageOptions.length) {
            setSelectedTemplateStageId("");
            return;
        }

        setSelectedTemplateStageId((prev) => {
            if (prev && templateStageOptions.some((s) => s.id === prev)) return prev;
            return templateStageOptions[0].id;
        });
    }, [templateStageOptions]);

    const publicScheduleUrl = useMemo(() => {
        if (!activeSeasonData?.slug) return null;

        const path = `/schedule/${activeSeasonData.slug}`;
        if (typeof window === "undefined") return path;

        return `${window.location.origin}${path}`;
    }, [activeSeasonData?.slug]);

    const handleOpenPublicSchedule = useCallback(() => {
        if (!publicScheduleUrl) return;

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard
                .writeText(publicScheduleUrl)
                .then(() => {
                    toast.info(t("fixtures.messages.publicScheduleCopied"), {
                        autoClose: 2500,
                        position: "top-center",
                        toastId: "public_schedule_copied",
                        closeOnClick: true,
                    });
                })
                .catch(() => {
                    // ignore clipboard errors silently
                });
        }

        if (typeof window !== "undefined") {
            window.open(publicScheduleUrl, "_blank", "noopener");
        }
    }, [publicScheduleUrl, t]);

    const handleOpenPrintGameSheetModal = useCallback(() => {
        const gameEvents = (allGames || []).filter((e) => e?.eventType === "GAME");

        if (!gameEvents.length) {
            toast.info(t("fixtures.messages.noGamesToPrint"), {
                autoClose: 2500,
                toastId: "no_games_to_print",
                position: "top-center",
            });
            return;
        }

        setPrintModalOpen(true);
    }, [allGames, t]);

    const handleImportGamesSuccess = useCallback(() => {
        fetchGames(page, {useAnchor: false});
    }, [fetchGames, page]);

    const planKeyRaw =
        activeSubscription?.plan?.key ||
        activeSubscription?.plan?.code ||
        activeSubscription?.plan?.name ||
        "";

    const normalizedPlanKey = String(planKeyRaw)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const canUseExcelImportTools = useMemo(() => {
        if (activeSubscription?.isTrial === true) return true;
        if (!normalizedPlanKey) return false;

        return (
            normalizedPlanKey.includes("elite") ||
            normalizedPlanKey.includes("enterprise") ||
            normalizedPlanKey.includes("trial")
        );
    }, [activeSubscription?.isTrial, normalizedPlanKey]);

    const showExcelPlanRestrictionToast = useCallback(() => {
        toast.info(t("fixtures.messages.excelToolsPlanRequired"), {
            autoClose: 3500,
            toastId: "fixtures_excel_tools_plan_required",
            position: "top-center",
            closeOnClick: true,
        });
    }, [t]);

    const handleOpenGamesTemplateModal = useCallback(() => {
        if (!canUseExcelImportTools) {
            showExcelPlanRestrictionToast();
            return;
        }

        if (!templateStageOptions.length) {
            toast.info(t("fixtures.messages.noStagesAvailable"), {
                autoClose: 2500,
                toastId: "no_stage_available_for_template",
                position: "top-center",
            });
            return;
        }

        setTemplateModalOpen(true);
    }, [canUseExcelImportTools, showExcelPlanRestrictionToast, t, templateStageOptions]);

    const handleOpenImportGamesModal = useCallback(() => {
        if (!canUseExcelImportTools) {
            showExcelPlanRestrictionToast();
            return;
        }

        if (window.$ && typeof window.$("#import-games-excel-modal")?.modal === "function") {
            window.$("#import-games-excel-modal").modal("show");
        }
    }, [canUseExcelImportTools, showExcelPlanRestrictionToast]);

    const handleDownloadGamesTemplate = useCallback(async () => {
        const stageId = Number(selectedTemplateStageId);
        if (!stageId) return;

        const maxRows = Math.max(1, parseInt(templateMaxRows || 400, 10));
        const isBlankTemplate = selectedTemplateType === "blank";

        try {
            const {data} = await (isBlankTemplate
                ? loadGamesBlankImportTemplate({
                    variables: {
                        stageId,
                        maxRows,
                    },
                })
                : loadGamesImportTemplate({
                    variables: {
                        stageId,
                        maxRows,
                    },
                }));

            const filePayload = isBlankTemplate
                ? data?.gamesBlankImportExcelTemplate
                : data?.gamesImportExcelTemplate;

            if (!filePayload?.contentBase64) {
                toast.error(t("fixtures.messages.failedToDownloadTemplate"), {
                    autoClose: 3000,
                    toastId: "download_template_missing_payload",
                    position: "top-center",
                    closeOnClick: true,
                });
                return;
            }

            const bytes = b64ToUint8Array(filePayload.contentBase64);
            const blob = new Blob([bytes], {
                type:
                    filePayload.mimeType ||
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");

            a.href = url;
            a.download =
                filePayload.filename ||
                (isBlankTemplate
                    ? "games-blank-import-template.xlsx"
                    : "games-import-template.xlsx");

            document.body.appendChild(a);
            a.click();
            a.remove();

            URL.revokeObjectURL(url);

            setTemplateModalOpen(false);

            toast.success(t("fixtures.messages.templateDownloaded"), {
                autoClose: 2500,
                toastId: "games_template_downloaded",
                position: "top-center",
                closeOnClick: true,
            });
        } catch (err) {
            toast.error(
                getLocalizedGraphQLError(err, t, "fixtures.messages.failedToDownloadTemplate"),
                {
                    autoClose: 3000,
                    toastId: "games_template_download_error",
                    position: "top-center",
                    closeOnClick: true,
                }
            );
        }
    }, [
        loadGamesBlankImportTemplate,
        loadGamesImportTemplate,
        selectedTemplateStageId,
        selectedTemplateType,
        t,
        templateMaxRows,
    ]);

    const scheduleActions = [
        {
            label: t("fixtures.buttons.addGames"),
            iconClass: "fas fa-plus",
            onClick: () => {
            },
            props: {
                "data-toggle": "modal",
                "data-target": "#bulk-add-games-modal",
                disabled: loading,
            },
        },
        {
            label: t("fixtures.buttons.addTrainings"),
            iconClass: "fas fa-dumbbell",
            onClick: () => {
            },
            props: {
                "data-toggle": "modal",
                "data-target": "#bulk-add-trainings-modal",
                disabled: loading,
            },
        },
        {
            label: t("fixtures.buttons.bulkEdit"),
            iconClass: "fas fa-edit",
            onClick: () => {},
            props: {
                "data-toggle": "modal",
                "data-target": "#bulk-edit-games-modal",
                disabled: loading,
            },
        },
        {
            label: t("fixtures.buttons.printSchedule"),
            iconClass: "fas fa-calendar-alt",
            onClick: handleOpenPublicSchedule,
            props: {
                disabled: loading,
                title: t("fixtures.tooltips.openPublicSchedule"),
            },
        },
        {
            label: t("fixtures.buttons.printGameSheet"),
            iconClass: "fas fa-clipboard-list",
            onClick: handleOpenPrintGameSheetModal,
            props: {
                disabled: loading,
                title: t("fixtures.tooltips.printOfficialGameSheets"),
            },
        },
        {
            label: t("fixtures.buttons.downloadGamesTemplate"),
            iconClass: "fas fa-file-excel",
            onClick: handleOpenGamesTemplateModal,
            props: {
                disabled: loading || templateLoading,
                title: t("fixtures.tooltips.downloadGamesTemplate"),
            },
        },
        {
            label: t("fixtures.buttons.importGamesExcel"),
            iconClass: "fas fa-file-upload",
            onClick: handleOpenImportGamesModal,
            props: {
                disabled: loading,
                title: t("fixtures.tooltips.importGamesExcel"),
            },
        },
    ];

    const selectableEvents = useMemo(
        () =>
            visibleGames
                .filter((event) => {
                    return event?.status === "SCHEDULED";
                })
                .map((event) => event.id),
        [visibleGames]
    );

    const toggleEventSelection = useCallback((eventId) => {
        if (!selectableEvents.includes(eventId)) return;

        setSelectedEventIds((prev) => {
            if (prev.includes(eventId)) {
                return prev.filter((id) => id !== eventId);
            }

            return [...prev, eventId];
        });
    }, [selectableEvents]);

    const selectedEvents = useMemo(() => {
        const selectedIdSet = new Set(selectedEventIds);
        return (allGames || []).filter((event) => selectedIdSet.has(event.id));
    }, [allGames, selectedEventIds]);

    const resolveAllMatchingScheduledEventIds = useCallback(async () => {
        if (!activeSeasonData?.slug) return [];

        const anchorDate = resolveAnchorDateISO();
        const allScheduledEventIds = new Set();
        const bulkPageSize = 200;
        let currentPage = 1;
        let totalPages = 1;

        try {
            while (currentPage <= totalPages) {
                const res = await getEventsSeasonAnchored({
                    variables: {
                        seasonSlug: activeSeasonData.slug,
                        stageId: undefined,
                        page: currentPage,
                        pageSize: bulkPageSize,
                        anchorDate,
                    },
                    fetchPolicy: "network-only",
                });

                const payload = res.data?.eventsBySeason;
                const objects = payload?.objects || [];

                objects
                    .filter((event) => event?.status === "SCHEDULED")
                    .forEach((event) => allScheduledEventIds.add(event.id));

                totalPages = Math.max(1, parseInt(payload?.pages || 1, 10));
                currentPage += 1;
            }

            return Array.from(allScheduledEventIds);
        } catch (err) {
            toast.error(
                getLocalizedGraphQLError(err, t, "bulkEditGamesModal.failedToResolveMatching"),
                {
                    autoClose: 3000,
                    toastId: "bulk_edit_matching_events_error",
                    position: "top-center",
                    closeOnClick: true,
                }
            );
            return [];
        }
    }, [activeSeasonData?.slug, getEventsSeasonAnchored, resolveAnchorDateISO, t]);

    const allSelected =
        selectableEvents.length > 0 &&
        selectableEvents.every((id) => selectedEventIds.includes(id));

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedEventIds([]);
        } else {
            setSelectedEventIds(selectableEvents);
        }
    };

    return (
        <>
            <div id="kbola-fixtures-scope">
                <PaginatedFlatTable
                    title={t("fixtures.title")}
                    page={page}
                    pages={pages}
                    onPageChange={(p) => fetchGames(p, {useAnchor: false})}
                    items={visibleGames}
                    loading={loading}
                    loadingOverlay
                    showPageNumbers
                    pageWindow={2}
                    headerRightExtras={
                        <div className="kbola-fixtures-header-tools">
                        <div className="kbola-page-size-wrap">
                            <label htmlFor="fixtures-page-size">
                                {t("fixtures.pageSize.label")}
                            </label>
                            <select
                                id="fixtures-page-size"
                                className="form-control"
                                value={pageSize}
                                onChange={(e) => {
                                    const nextPageSize = parseInt(e.target.value, 10);
                                    if (!Number.isFinite(nextPageSize)) return;
                                    setPageSize(nextPageSize);
                                    setSelectedEventIds([]);
                                }}
                                disabled={loading}
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={`fixtures_page_size_${size}`} value={size}>
                                        {t("fixtures.pageSize.option", {size})}
                                    </option>
                                ))}
                            </select>
                        </div>
                        </div>
                    }
                    addActions={scheduleActions}
                    headers={[
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                        />,
                        t("fixtures.headers.matchweek"),
                        t("fixtures.headers.stageGroup"),
                        t("fixtures.headers.away"),
                        "",
                        t("fixtures.headers.home"),
                        t("fixtures.headers.score"),
                        t("fixtures.headers.details"),
                        t("fixtures.headers.actions"),
                    ]}
                    renderRow={(event, idx) => {
                        if (event?.eventType === "GAME") {
                            return (
                                <GameElement
                                    key={`game_element_${event.id}_${idx}`}
                                    event={event}
                                    sportKey={division?.sport?.key}
                                    stages={stages}
                                    referees={division?.referees}
                                    games={allGames}
                                    updateScore={updateScore}
                                    handleRemoveGame={handleRemoveGame}
                                    selected={selectedEventIds.includes(event.id)}
                                    onToggleSelect={() => toggleEventSelection(event.id)}
                                    assignmentStatus={hasFeature("elite_referee_management_dashboard") && event.eventType === "GAME" ? getScheduleAssignmentStatus(event) : null}
                                />
                            );
                        }

                        if (event?.eventType === "TRAINING") {
                            return (
                                <TrainingElement
                                    key={`training_${event.id}_${idx}`}
                                    event={event}
                                    handleRemoveEvent={handleRemoveEvent}
                                    onEdit={() => {}}
                                    selected={selectedEventIds.includes(event.id)}
                                    onToggleSelect={() => toggleEventSelection(event.id)}
                                />
                            );
                        }

                        return null;
                    }}
                    addButtonTitle={t("fixtures.buttons.actions")}
                />

                <style>{`
          #kbola-fixtures-scope {
            font-family: 'Poppins', sans-serif;
          }

          #kbola-fixtures-scope i.fas,
          #kbola-fixtures-scope i.far,
          #kbola-fixtures-scope i.fal,
          #kbola-fixtures-scope i.fa,
          #kbola-fixtures-scope i[class^="fa-"],
          #kbola-fixtures-scope i[class*=" fa-"] {
            font-family: "Font Awesome 5 Free" !important;
            font-weight: 900;
          }

          #kbola-fixtures-scope i.fab {
            font-family: "Font Awesome 5 Brands" !important;
            font-weight: 400;
          }

          #kbola-fixtures-scope .card {
            border-radius: 18px;
            overflow: hidden;
            border: 1px solid rgba(0,0,0,0.08);
            box-shadow: 0 18px 44px rgba(0,0,0,0.10);
          }

          #kbola-fixtures-scope .card-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 12px 14px;
            background: linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.18), rgba(0,0,0,0));
            border-bottom: 1px solid rgba(0,0,0,0.06);
          }

          #kbola-fixtures-scope .card-header .card-title {
            font-size: 14px;
            font-weight: 900;
            color: #14151a;
            margin: 0;
          }

          #kbola-fixtures-scope .card-header .card-tools,
          #kbola-fixtures-scope .card-header .float-right,
          #kbola-fixtures-scope .card-header .btn-group {
            display: inline-flex;
            align-items: center;
            gap: 10px;
          }

          #kbola-fixtures-scope .card-header .btn {
            border-radius: 12px;
            padding: 10px 12px;
            font-weight: 900;
            border: 1px solid rgba(0,0,0,0.12);
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }

          #kbola-fixtures-scope .kbola-page-size-wrap {
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }

          #kbola-fixtures-scope .kbola-fixtures-header-tools {
            display: inline-flex;
            align-items: flex-end;
            gap: 12px;
          }

          #kbola-fixtures-scope .kbola-fixtures-assignment-filter {
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }

          #kbola-fixtures-scope .kbola-fixtures-assignment-tools {
            display: inline-flex;
            align-items: center;
            gap: 12px;
          }

          #kbola-fixtures-scope .kbola-fixtures-assignment-summary {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            flex-wrap: wrap;
          }

          #kbola-fixtures-scope .kbola-assignment-summary {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 4px 7px;
            border: 1px solid transparent;
            border-radius: 999px;
            font-size: 10px;
            font-weight: 800;
            white-space: nowrap;
          }

          #kbola-fixtures-scope .kbola-assignment-summary strong { font-size: 12px; }
          #kbola-fixtures-scope .kbola-assignment-summary.kbola-assignment-unassigned { color: #8a5a00; background: #fff7df; border-color: #f1d996; }
          #kbola-fixtures-scope .kbola-assignment-summary.kbola-assignment-manual { color: #5d4a8d; background: #f3efff; border-color: #ddd2fa; }
          #kbola-fixtures-scope .kbola-assignment-summary.kbola-assignment-proposed { color: #8a4f00; background: #fff2df; border-color: #f2d2a6; }
          #kbola-fixtures-scope .kbola-assignment-summary.kbola-assignment-invited { color: #1769aa; background: #eef7ff; border-color: #cfe7fa; }
          #kbola-fixtures-scope .kbola-assignment-summary.kbola-assignment-accepted { color: #16845a; background: #e9f8f0; border-color: #c8edda; }
          #kbola-fixtures-scope .kbola-assignment-summary.kbola-assignment-declined { color: #a33a46; background: #fff0f1; border-color: #f3c9ce; }
          #kbola-fixtures-scope .kbola-assignment-summary.kbola-assignment-completed { color: #176b62; background: #e7f7f4; border-color: #c5e8e1; }

          #kbola-fixtures-scope .kbola-fixtures-assignment-filter label {
            margin: 0;
            color: rgba(20,21,26,0.72);
            font-size: 12px;
            font-weight: 800;
            white-space: nowrap;
          }

          #kbola-fixtures-scope .kbola-fixtures-assignment-filter select.form-control {
            min-width: 150px;
            height: calc(1.9rem + 2px);
            padding: 0.25rem 0.5rem;
            border-radius: 10px;
            border: 1px solid rgba(0,0,0,0.12);
            font-size: 12px;
            font-weight: 800;
          }

          @media (max-width: 1100px) {
            #kbola-fixtures-scope .kbola-fixtures-assignment-summary { display: none; }
          }

          #kbola-fixtures-scope .kbola-assignment-pill {
            border: 1px solid transparent;
            font-weight: 900;
          }

          #kbola-fixtures-scope .kbola-assignment-unassigned {
            color: #8a5a00;
            background: #fff7df;
            border-color: #f1d996;
          }

          #kbola-fixtures-scope .kbola-assignment-invited {
            color: #1769aa;
            background: #eef7ff;
            border-color: #cfe7fa;
          }

          #kbola-fixtures-scope .kbola-assignment-manual {
            color: #5d4a8d;
            background: #f3efff;
            border-color: #ddd2fa;
          }

          #kbola-fixtures-scope .kbola-assignment-proposed {
            color: #8a4f00;
            background: #fff2df;
            border-color: #f2d2a6;
          }

          #kbola-fixtures-scope .kbola-assignment-accepted {
            color: #16845a;
            background: #e9f8f0;
            border-color: #c8edda;
          }

          #kbola-fixtures-scope .kbola-assignment-declined {
            color: #a33a46;
            background: #fff0f1;
            border-color: #f3c9ce;
          }

          #kbola-fixtures-scope .kbola-assignment-completed {
            color: #176b62;
            background: #e7f7f4;
            border-color: #c5e8e1;
          }

          #kbola-fixtures-scope .kbola-page-size-wrap label {
            margin: 0;
            font-size: 12px;
            font-weight: 800;
            color: rgba(20,21,26,0.72);
            white-space: nowrap;
          }

          #kbola-fixtures-scope .kbola-page-size-wrap select.form-control {
            min-width: 110px;
            height: calc(1.9rem + 2px);
            padding: 0.25rem 0.5rem;
            border-radius: 10px;
            font-weight: 800;
            border: 1px solid rgba(0,0,0,0.12);
          }

          #kbola-fixtures-scope .card-header .btn-kbola,
          #kbola-fixtures-scope .card-header .btn.btn-primary {
            background: ${GlobalStyles.colors.canary};
            border: 1px solid rgba(0,0,0,0.10);
            color: ${GlobalStyles.colors.woodsmoke};
          }

          #kbola-fixtures-scope .card-header .btn.btn-secondary,
          #kbola-fixtures-scope .card-header .btn.btn-default {
            background: rgba(0,0,0,0.02);
            border: 1px solid rgba(0,0,0,0.10);
            color: #14151a;
          }

          #kbola-fixtures-scope .card-header .btn:disabled {
            opacity: 0.65;
            cursor: not-allowed;
          }

          #kbola-fixtures-scope .table thead th {
            background: rgba(0,0,0,0.02);
            border-bottom: 1px solid rgba(0,0,0,0.06);
            color: rgba(20,21,26,0.72);
            font-size: 12.5px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.02em;
            padding: 12px 14px;
            vertical-align: middle;
          }

          #kbola-fixtures-scope .table tbody td {
            border-top: 1px solid rgba(0,0,0,0.06);
            padding: 12px 14px;
            vertical-align: middle;
          }

          #kbola-fixtures-scope .table tbody tr:hover {
            background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.10);
          }

          #kbola-fixtures-scope .pagination .page-link {
            border-radius: 10px;
            font-weight: 900;
            border: 1px solid rgba(0,0,0,0.10);
            color: rgba(20,21,26,0.80);
          }

          #kbola-fixtures-scope .pagination .page-item.active .page-link {
            background: ${GlobalStyles.colors.canary};
            border-color: rgba(0,0,0,0.10);
            color: ${GlobalStyles.colors.woodsmoke};
          }

          #kbola-fixtures-scope .overlay-wrapper {
            position: relative;
          }

          #kbola-fixtures-scope .overlay-wrapper > .overlay {
            border-radius: 14px;
            background: rgba(255,255,255,0.72);
            backdrop-filter: blur(2px);
          }

          #kbola-fixtures-scope button:focus,
          #kbola-fixtures-scope .btn:focus,
          #kbola-fixtures-scope .form-control:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
            border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
          }

          .kbola-template-modal .modal-content {
            border-radius: 18px;
            overflow: hidden;
            border: 1px solid rgba(0,0,0,0.08);
            box-shadow: 0 18px 44px rgba(0,0,0,0.12);
          }

          .kbola-template-modal .modal-header {
            border-bottom: 1px solid rgba(0,0,0,0.08);
            background: linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.22), rgba(0,0,0,0));
          }

          .kbola-template-modal .modal-title {
            font-size: 14px;
            font-weight: 900;
            color: #14151a;
          }

          .kbola-template-modal .modal-body label {
            font-size: 12px;
            font-weight: 800;
            color: rgba(20,21,26,0.76);
            margin-bottom: 8px;
          }

          .kbola-template-modal .form-control {
            border-radius: 12px;
            border: 1px solid rgba(0,0,0,0.14);
            min-height: 40px;
            font-weight: 700;
            color: #14151a;
          }

          .kbola-template-modal .form-control:focus,
          .kbola-template-modal .btn:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
            border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
          }

          .kbola-template-modal .modal-footer {
            border-top: 1px solid rgba(0,0,0,0.08);
            display: flex;
            gap: 8px;
          }

          .kbola-template-modal .btn {
            border-radius: 12px;
            padding: 10px 12px;
            font-weight: 900;
            border: 1px solid rgba(0,0,0,0.12);
          }

          .kbola-template-modal .btn.btn-default {
            background: rgba(0,0,0,0.02);
            border: 1px solid rgba(0,0,0,0.10);
            color: #14151a;
          }

          .kbola-template-modal .btn.btn-primary {
            background: ${GlobalStyles.colors.canary};
            border: 1px solid rgba(0,0,0,0.10);
            color: ${GlobalStyles.colors.woodsmoke};
          }

          .kbola-template-modal .btn:disabled {
            opacity: 0.65;
            cursor: not-allowed;
          }

          .kbola-template-backdrop {
            background: rgba(0,0,0,0.36);
          }

          .kbola-print-area {
            display: none;
          }

          @media print {
            #kbola-fixtures-scope {
              display: none !important;
            }

            .kbola-print-area {
              display: block;
              margin: 0;
              padding: 0;
            }

            .kbola-gamesheet-page {
              margin: 0 auto 24px auto;
            }

            @page {
              size: A4 portrait;
              margin: 8mm;
            }

            .kbola-print-area {
              margin: 0;
              padding: 0;
            }

            .kbola-gamesheet-page {
              width: 100%;
              min-height: 270mm;
              box-sizing: border-box;
              break-after: page;
              page-break-after: always;
              page-break-inside: avoid;
              margin: 0 auto;
            }
          }
        `}</style>
            </div>

            <BulkAddGamesModal
                modalId="bulk-add-games-modal"
                stages={stages}
                referees={division?.referees}
                managers={managerOptions}
                matchDays={groupedForModal}
            />

            <BulkAddTrainingsModal
                modalId="bulk-add-trainings-modal"
                stages={stages}
                referees={division?.referees}
                managers={managerOptions}
            />

            <BulkEditGamesModal
                modalId="bulk-edit-games-modal"
                selectedEvents={selectedEvents}
                referees={division?.referees}
                managers={managerOptions}
                stages={stages}
                onResolveAllMatchingEventIds={resolveAllMatchingScheduledEventIds}
                onUpdated={() => {
                    setSelectedEventIds([]);
                    fetchGames(page, {useAnchor: false});
                }}
            />

            <ImportGamesExcelModal
                modalId="import-games-excel-modal"
                stages={stages}
                referees={division?.referees}
                managers={managerOptions}
                onImported={handleImportGamesSuccess}
            />

            <PrintGameSheetsModal
                open={printModalOpen}
                games={(allGames || []).filter((e) => e?.eventType === "GAME")}
                division={division}
                activeSeasonData={activeSeasonData}
                onClose={() => setPrintModalOpen(false)}
                onConfirm={(selectedEvents) => {
                    if (!selectedEvents || !selectedEvents.length) {
                        setPrintModalOpen(false);
                        return;
                    }

                    setGamesToPrint(selectedEvents);
                    setPrintModalOpen(false);

                    setTimeout(() => {
                        window.print();
                    }, 0);
                }}
            />

            {templateModalOpen && (
                <>
                    <div
                        className="modal fade show kbola-template-modal"
                        style={{display: "block"}}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {t("fixtures.templateModal.title")}
                                    </h5>
                                    <button
                                        type="button"
                                        className="close"
                                        aria-label={t("common.cancel")}
                                        onClick={() => setTemplateModalOpen(false)}
                                        disabled={templateLoading}
                                    >
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>

                                <div className="modal-body">
                                    <div className="form-group">
                                        <label htmlFor="games-template-type-select">
                                            {t("fixtures.templateModal.templateTypeLabel")}
                                        </label>

                                        <select
                                            id="games-template-type-select"
                                            className="form-control"
                                            value={selectedTemplateType}
                                            onChange={(e) =>
                                                setSelectedTemplateType(e.target.value)
                                            }
                                            disabled={templateLoading}
                                        >
                                            <option value="dropdown">
                                                {t("fixtures.templateModal.dropdownTemplate")}
                                            </option>
                                            <option value="blank">
                                                {t("fixtures.templateModal.blankTemplate")}
                                            </option>
                                        </select>

                                        <small className="form-text text-muted">
                                            {selectedTemplateType === "blank"
                                                ? t("fixtures.templateModal.blankTemplateHint")
                                                : t("fixtures.templateModal.dropdownTemplateHint")}
                                        </small>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="games-template-stage-select">
                                            {t("fixtures.templateModal.stageLabel")}
                                        </label>
                                        <select
                                            id="games-template-stage-select"
                                            className="form-control"
                                            value={selectedTemplateStageId}
                                            onChange={(e) =>
                                                setSelectedTemplateStageId(e.target.value)
                                            }
                                            disabled={templateLoading}
                                        >
                                            <option value="">
                                                {t("fixtures.templateModal.selectStage")}
                                            </option>
                                            {templateStageOptions.map((stage) => (
                                                <option key={`template_stage_${stage.id}`} value={stage.id}>
                                                    {stage.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group mb-0">
                                        <label htmlFor="games-template-max-rows-input">
                                            {t("fixtures.templateModal.maxRowsLabel")}
                                        </label>
                                        <input
                                            id="games-template-max-rows-input"
                                            type="number"
                                            min="1"
                                            className="form-control"
                                            value={templateMaxRows}
                                            onChange={(e) => {
                                                const next = parseInt(e.target.value || 0, 10);
                                                setTemplateMaxRows(Number.isFinite(next) ? next : 400);
                                            }}
                                            disabled={templateLoading}
                                        />
                                        <small className="form-text text-muted">
                                            {t("fixtures.templateModal.maxRowsHint")}
                                        </small>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-default"
                                        onClick={() => setTemplateModalOpen(false)}
                                        disabled={templateLoading}
                                    >
                                        {t("common.cancel")}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleDownloadGamesTemplate}
                                        disabled={templateLoading || !selectedTemplateStageId}
                                    >
                                        {templateLoading
                                            ? t("fixtures.templateModal.generating")
                                            : t("fixtures.templateModal.download")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-backdrop fade show kbola-template-backdrop"/>
                </>
            )}

            {printRoot &&
                createPortal(
                    <div>
                        {(gamesToPrint || []).map((event) => (
                            <GameSheet
                                key={`print_gamesheet_${event.id}`}
                                event={event}
                                division={division}
                                activeSeasonData={activeSeasonData}
                            />
                        ))}
                    </div>,
                    printRoot
                )}

            {(allGames || []).map((event, idx) =>
                event?.eventType === "GAME" && event?.game ? (
                    <EditGameModal
                        key={`editGameModal_${event.id}_${idx}`}
                        id={`edit-game-modal${event.id}`}
                        games={allGames}
                        stages={stages}
                        event={event}
                        referees={event.stage?.stageReferees || []}
                        managers={managerOptions}
                    />
                ) : null
            )}

            {(allGames || []).map((event, idx) =>
                event?.eventType === "GAME" && event?.game ? (
                    <GameResultModal
                        key={`gameResultModal_${event.id}_${idx}`}
                        id={`game-result-modal${event.id}`}
                        game={event.game}
                        event={event}
                        sportKey={division?.sport?.key}
                        updateScore={updateScore}
                    />
                ) : null
            )}
        </>
    );
}
